import React from 'react';
import {
  Box,
  Typography,
  Chip,
  useTheme
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import { InfoCard } from './InfoCard';
import type { Resident } from '../../types/resident.types';
import { 
  getAttributeValue, 
  getCnhNumberFromAttributes, 
  getCnhExpirationFromAttributes
} from '../../../../utils/getAttributeLabelAndValue';

interface ResidentInfoProps {
  resident: Resident;
}

export function ResidentInfo({ resident }: ResidentInfoProps) {
  const theme = useTheme();
  
  const formatDate = (dateString: string) => {
    if (!dateString) return 'Não informado';
    return new Date(dateString).toLocaleDateString('pt-BR');
  };
console.log(resident);
  const getGenderLabel = (gender: string) => {
    switch (gender) {
      case 'MALE': return 'Masculino';
      case 'FEMALE': return 'Feminino';
      default: return gender || 'Não informado';
    }
  };

  const getMaritalStatusLabel = (status: string) => {
    switch (status) {
      case 'MARRIED': return 'Casado';
      case 'SINGLE': return 'Solteiro';
      case 'DIVORCED': return 'Divorciado';
      default: return status || 'Não informado';
    }
  };

  // Extrair valores dos attributes usando as funções utilitárias
  const document = getAttributeValue(resident.attributes, 'document');
  const phoneNumber = getAttributeValue(resident.attributes, 'phone_number');
  const birthDate = getAttributeValue(resident.attributes, 'birth_date');
  const gender = getAttributeValue(resident.attributes, 'gender_flag');
  const maritalStatus = getAttributeValue(resident.attributes, 'marital_flag');
  const statusFlag = getAttributeValue(resident.attributes, 'status_flag');
  const nationality = getAttributeValue(resident.attributes, 'nationality');
  
  
  const cnhNumber = getCnhNumberFromAttributes(resident.attributes);
  const cnhExpiration = getCnhExpirationFromAttributes(resident.attributes);
  
 
  const unitAttr: { number?: string | number; tower_name?: string } | string | null = getAttributeValue(resident.attributes, 'unit') 
    || getAttributeValue(resident.attributes, 'apartment') 
    || '';

const sectionAttr = getAttributeValue(resident.attributes, 'section_name') 
  || getAttributeValue(resident.attributes, 'section') 
  || '';

let unitText = '';

// vai sair tower_name e number

if (typeof unitAttr === 'string') {
  unitText = unitAttr;
} else if (typeof unitAttr === 'object' && unitAttr !== null) {
  const number = unitAttr?.number  
  const tower = unitAttr?.tower_name || '';
  unitText = `${number}${tower ? ` - ${tower}` : ''}`;
}


const sectionText = typeof sectionAttr === 'string'
  ? sectionAttr
  : (sectionAttr?.value || '');

const unitInfo = unitText
  ? `${unitText}${sectionText ? ` - ${sectionText}` : ''}`
  : 'Não informado';



  return (
    <>
   
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 2, 
        mb: 2, 
        background: theme.palette.mode === 'dark' ? 'rgba(108, 99, 255, 0.1)' : '#F8FAFF', 
        borderRadius: 3, 
        p: 2 
      }}>
        <Box sx={{ 
          width: 48, 
          height: 48, 
          borderRadius: '50%', 
          background: theme.palette.primary.main, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center' 
        }}>
          {resident?.imageUrl ? (
            <img 
              src={resident?.imageUrl} 
              alt={resident.name}
              style={{ 
                width: '100%', 
                height: '100%', 
                objectFit: 'cover', 
                borderRadius: '50%' 
              }}
            />
          ) : (
            <PersonIcon sx={{ fontSize: 28, color: 'white' }} />
          )}
        </Box>
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Chip label="Morador" color="primary" size="small" variant="outlined" />
            <Chip 
              label={statusFlag === 'ACTIVE' ? 'Ativo' : 'Inativo'} 
              color={statusFlag === 'ACTIVE' ? 'success' : 'error'} 
              size="small" 
            />
          </Box>
          <Typography variant="h6" fontWeight={700} color={theme.palette.text.primary} mb={0.5}>
            {resident.name}
          </Typography>
          <Typography color={theme.palette.text.secondary} fontSize={14}>
            {resident.email} • {document}
          </Typography>
        </Box>
      </Box>

      {/* Informações em Grid */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 3 }}>
        <InfoCard label="Nome Completo" value={resident.name} />
        <InfoCard label="E-mail" value={resident.email} />
        <InfoCard 
          label="CPF" 
          value={document}
          customValue={
            <Typography component="span" sx={{ 
              fontFamily: 'monospace', 
              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : '#E5E7EB', 
              color: theme.palette.text.primary, 
              px: 2, 
              py: 0.5, 
              borderRadius: 2, 
              fontSize: 16, 
              display: 'inline-block', 
              fontWeight: 700 
            }}>
              {resident.documentNumber || 'Não informado'}
            </Typography>
          }
        />
        <InfoCard label="Telefone" value={resident.phoneNumber || 'Não informado'} />
        <InfoCard 
          label="Data de Nascimento" 
          value={birthDate ? formatDate(birthDate) : 'Não informado'} 
        />
        <InfoCard label="Gênero" value={getGenderLabel(gender)} />
        <InfoCard label="Estado Civil" value={getMaritalStatusLabel(maritalStatus)} />
        <InfoCard label="Nacionalidade" value={nationality || 'Não informado'} />
        <InfoCard label="Unidade" value={unitInfo} />
        {cnhNumber && (
          <InfoCard label="CNH" value={cnhNumber} />
        )}
        {cnhExpiration && (
          <InfoCard label="Vencimento CNH" value={formatDate(cnhExpiration)} />
        )}
        <InfoCard label="Empresa" value={resident.tenants?.[0]?.name || 'Não informado'} />
      </Box>
    </>
  );
}